import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Global loading state manager hook
 * Provides centralized loading state management with support for multiple concurrent operations
 */
export const useGlobalLoading = () => {
  const [loadingStates, setLoadingStates] = useState({});
  const [globalMessage, setGlobalMessage] = useState("");
  const timeoutRefs = useRef({});

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  /**
   * Start loading for a specific operation
   */
  const startLoading = useCallback(
    (key, message = "Loading...", timeout = 30000) => {
      setLoadingStates((prev) => ({
        ...prev,
        [key]: {
          isLoading: true,
          message,
          startTime: Date.now(),
        },
      }));

      // Set global message if this is the first loading operation
      setGlobalMessage((prev) => prev || message);

      // Set timeout to automatically stop loading (prevents stuck loading states)
      if (timeout > 0) {
        if (timeoutRefs.current[key]) {
          clearTimeout(timeoutRefs.current[key]);
        }

        timeoutRefs.current[key] = setTimeout(() => {
          console.warn(
            `Loading operation "${key}" timed out after ${timeout}ms`,
          );
          stopLoading(key);
        }, timeout);
      }
    },
    [],
  );

  /**
   * Stop loading for a specific operation
   */
  const stopLoading = useCallback(
    (key) => {
      setLoadingStates((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      // Clear timeout
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]);
        delete timeoutRefs.current[key];
      }

      // Update global message
      setGlobalMessage((prev) => {
        const remainingStates = Object.values(loadingStates).filter(
          (state) => state.isLoading,
        );
        if (remainingStates.length === 0) {
          return "";
        }
        // Use the message from the most recent loading operation
        const mostRecent = remainingStates.reduce((latest, current) =>
          current.startTime > latest.startTime ? current : latest,
        );
        return mostRecent.message;
      });
    },
    [loadingStates],
  );

  /**
   * Check if a specific operation is loading
   */
  const isLoading = useCallback(
    (key) => {
      return loadingStates[key]?.isLoading || false;
    },
    [loadingStates],
  );

  /**
   * Check if any operation is loading
   */
  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some((state) => state.isLoading);
  }, [loadingStates]);

  /**
   * Get loading message for a specific operation
   */
  const getLoadingMessage = useCallback(
    (key) => {
      return loadingStates[key]?.message || "";
    },
    [loadingStates],
  );

  /**
   * Get all current loading operations
   */
  const getLoadingOperations = useCallback(() => {
    return Object.entries(loadingStates)
      .filter(([, state]) => state.isLoading)
      .map(([key, state]) => ({
        key,
        message: state.message,
        duration: Date.now() - state.startTime,
      }));
  }, [loadingStates]);

  /**
   * Clear all loading states
   */
  const clearAllLoading = useCallback(() => {
    // Clear all timeouts
    Object.values(timeoutRefs.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    timeoutRefs.current = {};

    setLoadingStates({});
    setGlobalMessage("");
  }, []);

  /**
   * Wrap an async operation with loading state management
   */
  const withLoading = useCallback(
    async (key, operation, message = "Loading...", timeout = 30000) => {
      try {
        startLoading(key, message, timeout);
        const result = await operation();
        return result;
      } finally {
        stopLoading(key);
      }
    },
    [startLoading, stopLoading],
  );

  /**
   * Create a loading wrapper for a specific key
   */
  const createLoadingWrapper = useCallback(
    (key, defaultMessage = "Loading...") => {
      return {
        start: (message = defaultMessage, timeout = 30000) =>
          startLoading(key, message, timeout),
        stop: () => stopLoading(key),
        isLoading: () => isLoading(key),
        getMessage: () => getLoadingMessage(key),
        withLoading: (operation, message = defaultMessage, timeout = 30000) =>
          withLoading(key, operation, message, timeout),
      };
    },
    [startLoading, stopLoading, isLoading, getLoadingMessage, withLoading],
  );

  return {
    // State queries
    isLoading,
    isAnyLoading,
    getLoadingMessage,
    getLoadingOperations,
    globalMessage,

    // State management
    startLoading,
    stopLoading,
    clearAllLoading,

    // Utilities
    withLoading,
    createLoadingWrapper,

    // Raw state (for debugging)
    loadingStates,
  };
};

/**
 * Common loading keys for consistent usage across the application
 */
export const LOADING_KEYS = {
  // Authentication
  LOGIN: "auth.login",
  LOGOUT: "auth.logout",
  PASSWORD_CHANGE: "auth.passwordChange",

  // Registration
  REGISTRATION_SUBMIT: "registration.submit",
  EMAIL_CHECK: "registration.emailCheck",
  FILE_UPLOAD: "registration.fileUpload",

  // Admin operations
  LOAD_REGISTRATIONS: "admin.loadRegistrations",
  APPROVE_REGISTRATION: "admin.approveRegistration",
  REJECT_REGISTRATION: "admin.rejectRegistration",
  LOAD_STATISTICS: "admin.loadStatistics",

  // Email operations
  SEND_EMAIL: "email.send",

  // File operations
  UPLOAD_FILE: "file.upload",
  DELETE_FILE: "file.delete",

  // Data operations
  SAVE_DATA: "data.save",
  LOAD_DATA: "data.load",
  DELETE_DATA: "data.delete",

  // General
  INITIALIZE: "app.initialize",
  REFRESH: "app.refresh",
};
